import * as Sentry from '@sentry/react'

type CaptureOptions = {
  tags?: Record<string, string | number | boolean | undefined>
  extra?: Record<string, unknown>
}

const ignoredConsoleMessages = [
  'Sentry initialization failed',
  'Sentry handled error capture failed',
  'Sentry console error capture failed',
]

let isSentryEnabled = false
let isConsoleErrorPatched = false
let isCapturingConsoleError = false

export const sentryRelease = process.env.REACT_APP_RELEASE ?? 'frontend'

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error

  try {
    return JSON.stringify(error) ?? Object.prototype.toString.call(error)
  } catch {
    return Object.prototype.toString.call(error)
  }
}

const toError = (error: unknown) => {
  if (error instanceof Error) return error
  return new Error(getErrorMessage(error))
}

const serializeConsoleArg = (arg: unknown) => {
  if (arg instanceof Error) {
    const stack = arg.stack ? `\n${arg.stack}` : ''
    return `${arg.name}: ${arg.message}${stack}`
  }

  if (typeof arg === 'string') return arg

  try {
    return JSON.stringify(arg) ?? Object.prototype.toString.call(arg)
  } catch {
    return Object.prototype.toString.call(arg)
  }
}

const shouldIgnoreConsoleError = (args: unknown[]) => {
  const firstArg = args[0]
  if (typeof firstArg !== 'string') return false

  return ignoredConsoleMessages.some((message) => firstArg.includes(message))
}

export const captureHandledError = (error: unknown, options?: CaptureOptions) => {
  if (!isSentryEnabled) return

  try {
    Sentry.withScope((scope) => {
      scope.setTag('handled', true)

      Object.entries(options?.tags ?? {}).forEach(([key, value]) => {
        if (value !== undefined) scope.setTag(key, value)
      })

      Object.entries(options?.extra ?? {}).forEach(([key, value]) => {
        scope.setExtra(key, value)
      })

      Sentry.captureException(toError(error))
    })
  } catch (captureError) {
    console.warn('Sentry handled error capture failed:', captureError)
  }
}

const patchConsoleError = () => {
  if (isConsoleErrorPatched) return

  const originalConsoleError = console.error

  console.error = (...args: unknown[]) => {
    originalConsoleError(...args)

    if (!isSentryEnabled || isCapturingConsoleError || shouldIgnoreConsoleError(args)) return

    isCapturingConsoleError = true
    try {
      const errorArg = args.find((arg) => arg instanceof Error)
      const error = errorArg ?? new Error(args.map(serializeConsoleArg).join(' '))

      captureHandledError(error, {
        tags: {
          source: 'console.error',
        },
        extra: {
          consoleArguments: args.map(serializeConsoleArg).slice(0, 5),
        },
      })
    } catch (captureError) {
      console.warn('Sentry console error capture failed:', captureError)
    } finally {
      isCapturingConsoleError = false
    }
  }

  isConsoleErrorPatched = true
}

export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN
  const debug = process.env.REACT_APP_SENTRY_DEBUG ?? false

  if (!dsn) {
    isSentryEnabled = false
    return false
  }

  try {
    Sentry.init({
      dsn,
      release: sentryRelease,
      sendDefaultPii: true,
      tracesSampleRate: 0.1,
      debug: debug === 'true',
      environment: process.env.NODE_ENV,
      attachStacktrace: true,
      normalizeDepth: 6,
      beforeSend(event) {
        event.tags = {
          ...event.tags,
          release: sentryRelease,
        }

        return event
      },
    })

    Sentry.setTag('release', sentryRelease)
    patchConsoleError()
    isSentryEnabled = true

    return true
  } catch (error) {
    isSentryEnabled = false
    console.warn('Sentry initialization failed:', error)

    return false
  }
}

export const getIsSentryEnabled = () => isSentryEnabled
